import { useToast } from '@app/core/notifications';
import {
  useYoutubeIntegrationInsertCuepointCuepoints,
  YoutubeIntegrationInsertCuepointCuepointsMutationRequest,
} from '@mi-front/api-v2';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { youtubeCuepointsMessages } from '../messages/youtube-cuepoints-messages';

interface UseInsertYoutubeCuepointOptions {
  stationId: string;
  listId: string;
  onSuccess?: () => void;
}

/**
 * Insert body shape accepted by youtube-integration (OpenAPI currently types the request as string).
 */
type InsertCuepointBody = {
  durationSecs: number;
  liveVideoId?: string;
  insertionOffsetTimeMs?: number;
  walltimeMs?: number;
};

/**
 * Wraps the youtube-integration insert-cuepoint mutation with list invalidation and user feedback.
 */
export function useInsertYoutubeCuepoint({
  stationId,
  listId,
  onSuccess,
}: UseInsertYoutubeCuepointOptions) {
  const { $t } = useIntl();
  const { notifySuccess, notifyError } = useToast();
  const queryClient = useQueryClient();

  const mutation = useYoutubeIntegrationInsertCuepointCuepoints();

  const insertCuepoint = async (durationSecs: number) => {
    try {
      const body: InsertCuepointBody = { durationSecs };
      await mutation.mutateAsync({
        station: stationId,
        // OpenAPI marks the body as string; runtime expects the insert DTO object.
        data: body as unknown as YoutubeIntegrationInsertCuepointCuepointsMutationRequest,
      });

      await queryClient.invalidateQueries({ queryKey: [listId] });

      notifySuccess($t(youtubeCuepointsMessages.insertSuccess));
      onSuccess?.();
    } catch (error) {
      notifyError($t(youtubeCuepointsMessages.insertError));
      console.error('Failed to insert YouTube cuepoint:', error);
      throw error;
    }
  };

  return {
    insertCuepoint,
    isPending: mutation.isPending,
  };
}
